<template>
<div>
    <validation-observer ref="observer" v-slot="{ passes }">
    <b-form @submit.prevent="passes(onFormSubmit)" novalidate>
        <b-form-group :disabled="!canEdit">

            <h6 class="text-muted mb-3">Obstetric overview</h6>
            <b-row>
                <b-col md="3">
                    <validation-provider vid="gravida" v-slot="{ errors }">
                        <b-form-group label="Gravida" label-for="gravida" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-input id="gravida" name="gravida" type="number" min="0" v-model.number="form.gravida" />
                        </b-form-group>
                    </validation-provider>
                </b-col>
                <b-col md="3">
                    <validation-provider vid="para" v-slot="{ errors }">
                        <b-form-group label="Para" label-for="para" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-input id="para" name="para" type="number" min="0" v-model.number="form.para" />
                        </b-form-group>
                    </validation-provider>
                </b-col>
                <b-col md="3">
                    <validation-provider vid="abortus" v-slot="{ errors }">
                        <b-form-group label="Abortus" label-for="abortus" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-input id="abortus" name="abortus" type="number" min="0" v-model.number="form.abortus" />
                        </b-form-group>
                    </validation-provider>
                </b-col>
                <b-col md="3">
                    <validation-provider vid="mater" v-slot="{ errors }">
                        <b-form-group label="Mater" label-for="mater" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-input id="mater" name="mater" type="number" min="0" v-model.number="form.mater" />
                        </b-form-group>
                    </validation-provider>
                </b-col>
            </b-row>

            <h6 class="text-muted mt-2 mb-3">Dating</h6>
            <b-row>
                <b-col md="4">
                    <validation-provider vid="last_menstruation_date" v-slot="{ errors }">
                        <b-form-group label="Last menstruation (LMP)" label-for="last_menstruation_date" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-datepicker id="last_menstruation_date" v-model="form.last_menstruation_date" :locale="currentLang" :max="today" reset-button placeholder="Set LMP..." />
                        </b-form-group>
                    </validation-provider>
                </b-col>
                <b-col md="2" class="d-flex align-items-center">
                    <b-form-checkbox id="lm_certain" v-model="form.lm_certain" switch>LMP certain</b-form-checkbox>
                </b-col>
                <b-col md="3">
                    <validation-provider vid="cycle_weeks" v-slot="{ errors }">
                        <b-form-group label="Cycle weeks" label-for="cycle_weeks" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-input id="cycle_weeks" name="cycle_weeks" type="number" min="0" v-model.number="form.cycle_weeks" />
                        </b-form-group>
                    </validation-provider>
                </b-col>
                <b-col md="3">
                    <validation-provider vid="cycle_days" v-slot="{ errors }">
                        <b-form-group label="Cycle days" label-for="cycle_days" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-input id="cycle_days" name="cycle_days" type="number" min="0" max="6" v-model.number="form.cycle_days" />
                        </b-form-group>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="2" class="d-flex align-items-center mb-3">
                    <b-form-checkbox id="cycle_regular" v-model="form.cycle_regular" switch>Cycle regular</b-form-checkbox>
                </b-col>
            </b-row>

            <h6 class="text-muted mt-2 mb-3">Expected delivery date</h6>
            <b-row>
                <b-col md="4">
                    <validation-provider vid="expected_delivery_date" v-slot="{ errors }">
                        <b-form-group label="EDD" label-for="expected_delivery_date" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                            <b-form-datepicker id="expected_delivery_date" v-model="form.expected_delivery_date" :locale="currentLang" reset-button placeholder="Set EDD..." />
                        </b-form-group>
                    </validation-provider>
                </b-col>
                <b-col md="8" class="d-flex align-items-center">
                    <div v-if="eddFromLmp" class="small text-muted">
                        EDD from LMP (Naegele, cycle-adjusted): <strong>{{ toLocaleDate(eddFromLmp) }}</strong>
                        <b-button v-if="canEdit" size="sm" variant="outline-primary" class="ml-2" @click="applyEddFromLmp">Apply</b-button>
                    </div>
                </b-col>
            </b-row>

            <h6 class="text-muted mt-2 mb-3">Notes</h6>
            <validation-provider vid="notes" v-slot="{ errors }">
                <b-form-group label-for="notes" :invalid-feedback="errors[0]" :state="errors[0] ? false : null">
                    <b-form-textarea id="notes" name="notes" rows="3" max-rows="8" placeholder="Clinical notes..." v-model="form.notes" />
                </b-form-group>
            </validation-provider>
        </b-form-group>

        <b-button v-if="canEdit" class="mr-1" :disabled="!isDirty" type="submit" variant="success">
            <i class="fa-regular fa-circle-dot"></i> {{ record ? 'Save' : 'Register pregnancy' }}
        </b-button>
        <b-button v-if="canEdit && (isDirty || hasErrors)" type="button" variant="danger" @click="reset">
            <i class="fa-solid fa-ban"></i> Reset
        </b-button>
    </b-form>
    </validation-observer>
</div>
</template>
<script>
import FormExtension from "@/core/components/FormExtension"
import { mapActions } from "vuex"

export default FormExtension.extend({
    name: "maternity-record-form",
    props: {
        record: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            today: new Date(),
            form: new Form({
                gravida: null,
                para: null,
                abortus: null,
                mater: null,
                last_menstruation_date: null,
                lm_certain: false,
                cycle_regular: false,
                cycle_weeks: null,
                cycle_days: null,
                expected_delivery_date: null,
                notes: "",
                consistency_token: null,
            }),
        }
    },
    computed: {
        canEdit() {
            return this.checkUserPermission('maternity', this.record ? 'can_change' : 'can_add')
        },
        eddFromLmp() {
            if (!this.form.last_menstruation_date) {
                return null
            }
            const lmp = new Date(this.form.last_menstruation_date)
            if (isNaN(lmp.getTime())) {
                return null
            }
            // cycle length in days; default 28 when not specified
            const weeks = Number(this.form.cycle_weeks) || 4
            const days = Number(this.form.cycle_days) || 0
            const cycleLength = weeks * 7 + days
            const edd = new Date(lmp.getTime())
            edd.setDate(edd.getDate() + 280 + (cycleLength - 28))
            return edd
        },
    },
    watch: {
        record: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.form.withData(val)
                } else {
                    this.form.reset()
                }
            },
        },
        form: {
            deep: true,
            handler() {
                this.isDirty = this.form.dirty()
                if (this.isDirty) {
                    this.dirty()
                } else {
                    this.validated()
                }
            },
        },
    },
    methods: {
        ...mapActions('maternity', ['createMaternityRecord', 'updateMaternityRecord']),
        applyEddFromLmp() {
            if (this.eddFromLmp) {
                // store as YYYY-MM-DD for the date field / API
                this.form.expected_delivery_date = this.eddFromLmp.toISOString().slice(0, 10)
            }
        },
        onFormSubmit() {
            const action = this.record
                ? this.updateMaternityRecord({ ...this.form.data(), id: this.record.id })
                : this.createMaternityRecord(this.form.data())

            action
                .then(() => {
                    this.reset()
                    this.notify(
                        this.record ? "Maternity record updated" : "Pregnancy registered",
                        this.record ? "The maternity record was saved successfully" : "The maternity record was created successfully"
                    )
                })
                .catch(errors => {
                    if (errors.response) {
                        this.$refs.observer?.setErrors(errors.response.data)
                        this.hasErrors = true
                    }
                })
        },
        reset() {
            if (this.record) {
                this.form.withData(this.record)
            } else {
                this.form.reset()
            }
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
                this.validated()
            })
        },
    },
})
</script>
